import React from 'react';
import { motion } from 'framer-motion';
import { Star, CheckCircle2, Clock, Users, Zap, Gift, Heart, Receipt, FastForward, Shield } from 'lucide-react';
import { BRAND } from '../../data/brand';
import { useTranslation } from '../../hooks/useTranslation';
import { useTranslatedServices } from '../../data/services';

export default function Services() {
  const { t, currentLanguage } = useTranslation();
  const services = useTranslatedServices();
  
  // Функция для получения правильного пути в зависимости от языка
  const getLocalizedPath = (path) => {
    if (currentLanguage === 'ru') {
      return path === '/' ? '/ru' : `/ru${path}`;
    }
    return path;
  };
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  const getServiceIcon = (title) => {
    if (title.includes('1 сеанс')) return <Clock className="h-6 w-6 text-rose-600" />;
    if (title.includes('2 сеанса')) return <Users className="h-6 w-6 text-rose-600" />;
    if (title.includes('3 сеанса')) return <Zap className="h-6 w-6 text-rose-600" />;
    if (title.includes('4 сеанса')) return <Star className="h-6 w-6 text-rose-600" />;
    if (title.includes('студентов')) return <Heart className="h-6 w-6 text-rose-600" />;
    return <Clock className="h-6 w-6 text-rose-600" />;
  };

  return (
    <section id="services" className="section-light">
      <div className="container-spacious">
        {/* Заголовок секции */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <div className="inline-flex items-center gap-2 bg-slate-50 px-6 py-2 rounded-full border border-slate-200 mb-6">
            <Star className="h-5 w-5 text-rose-600" />
            <span className="text-sm font-medium text-slate-700">{t('services.subtitle')}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-dark-900">
            {t('services.title')}
          </h2>
          <p className="text-base sm:text-lg max-w-2xl mx-auto text-gray-600">
            {t('contact.working_hours')}, {t('contact.weekend')}
          </p>
        </motion.div>

        {/* Тарифные планы */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10 items-stretch"
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              variants={item}
              className="relative group h-full"
            >
              <div className={`relative h-full card-elevated overflow-hidden flex flex-col ${
                service.popular ? 'ring-2 ring-rose-300' : ''
              }`}>
                
                {/* Заголовок карточки */}
                <div className={`p-4 sm:p-6 ${service.popular ? 'bg-rose-600 text-white' : 'bg-slate-50 text-slate-800'} relative`}>
                  {/* Бейджи */}
                  <div className="absolute top-4 right-4 flex flex-col gap-2">
                    {service.popular && (
                      <span className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-medium">
                        {t('services.popular')}
                      </span>
                    )}
                  </div>

                  {/* Бонус */}
                  {service.bonus && (
                    <div className="mb-4 p-3 bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-xl">
                      <div className="flex items-center gap-2 mb-1">
                        <Gift className="h-4 w-4 text-rose-600" />
                        <span className="text-sm font-semibold text-rose-800">{t('services.bonus_title')}</span>
                      </div>
                      <p className="text-sm text-rose-700">{service.bonus}</p>
                    </div>
                  )}

                  {/* Иконка и заголовок */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-xl ${service.popular ? 'bg-white/20' : 'bg-rose-100'}`}>
                      {getServiceIcon(service.title)}
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold">
                      {service.title}
                    </h3>
                  </div>

                  {/* Цена */}
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl sm:text-3xl font-bold">{service.price}</span>
                    <span className={`text-xs sm:text-sm ${service.popular ? 'text-white/80' : 'text-slate-500'} ${service.price === '€1000' ? 'font-bold text-rose-600' : ''}`}>
                      {service.price === '€320' || service.price === '€150' ? t('services.price_per_meeting') : 
                       service.price === '€1000' ? <span className="font-bold text-rose-600">{t('services.price_per_sessions')}</span> : t('services.price_per_package')}
                    </span>
                  </div>
                </div>

                {/* Содержимое карточки */}
                <div className="p-4 sm:p-6 flex flex-col flex-grow">
                  {/* Описание */}
                  <p className="text-slate-600 mb-6 text-sm leading-relaxed">
                    {service.desc.split('**').map((part, index) => 
                      index % 2 === 1 ? (
                        <strong key={index} className="text-rose-600 font-semibold">{part}</strong>
                      ) : (
                        part
                      )
                    )}
                  </p>

                  {/* Список включенного */}
                  <div className="space-y-3 mb-8 flex-grow">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-rose-600" />
                      {t('services.what_included')}:
                    </h4>
                    <ul className="space-y-2">
                      {service.bullets.map((bullet, bulletIndex) => (
                        <li key={bulletIndex} className="flex gap-3 items-start">
                          <div className="w-1.5 h-1.5 bg-rose-500 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-sm text-slate-600">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Кнопка записи */}
                  <a
                    href={getLocalizedPath("/contact")}
                    className={`w-full text-center block py-3 sm:py-4 px-4 sm:px-6 rounded-2xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl text-sm sm:text-base ${
                      service.popular
                        ? 'bg-rose-600 hover:bg-rose-700 text-white'
                        : 'bg-rose-100 hover:bg-rose-200 text-rose-800'
                    }`}
                  >
                    {service.popular ? t('services.book_now') : t('services.book')}
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Что вам полезно знать */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12"
        >
          <div className="rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-dark-900 mb-4 text-center">
              {t('services.what_for_you')}
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-rose-50 to-pink-50 p-6 rounded-2xl border-2 border-rose-100 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:border-rose-200">
                <Receipt className="w-6 h-6 text-rose-500 mx-auto mb-3" />
                <span className="text-base text-slate-800 font-medium">{t('services.tax_deductible')}</span>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border-2 border-blue-100 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:border-blue-200">
                <FastForward className="w-6 h-6 text-blue-500 mx-auto mb-3" />
                <span className="text-base text-slate-800 font-medium">{t('services.quick_appointment')}</span>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl border-2 border-green-100 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:border-green-200">
                <Shield className="w-6 h-6 text-green-500 mx-auto mb-3" />
                <span className="text-base text-slate-800 font-medium">{t('services.data_security')}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Дополнительная информация */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="block-primary text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Gift className="h-5 w-5 sm:h-6 sm:w-6 text-rose-600" />
              <h3 className="text-xl sm:text-2xl font-bold text-dark-900">
                {t('services.not_sure.title')}
              </h3>
            </div>
            <p className="text-base sm:text-lg mb-6 max-w-2xl mx-auto text-gray-600">
              {t('services.not_sure.description')}
            </p>
            <a
              href={getLocalizedPath("/contact")}
              className="btn-primary inline-flex items-center gap-3"
            >
              <Star className="h-5 w-5" />
              {t('services.not_sure.button')}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
